import {
  YearData,
  PeriodData,
  ChartDataPoint,
  MetricType,
  PeriodType,
  MerchantInsightsMetrics,
} from '../store/modules/insights/types';
import { formatCurrency, formatNumber, formatPercentage } from './formatters';

export const getQuarterMonthLabel = (label: string) => {
  switch (label) {
    case 'Q1':
      return '(Jan-Mar)';
    case 'Q2':
      return '(Apr-Jun)';
    case 'Q3':
      return '(Jul-Sep)';
    case 'Q4':
      return '(Oct-Dec)';
  }
  return '';
};

export const transformDataForChart = (
  yearData: YearData,
  metricType: MetricType,
  periodType: PeriodType,
): ChartDataPoint[] => {
  const periods = periodType === 'monthly' ? yearData.months : yearData.quarters;

  return periods.map((period: PeriodData) => ({
    period: period.label,
    value: period.metrics ? getMetricValue(period.metrics, metricType) : null,
    label: period.label,
    isCurrentPeriod: period.isCurrentPeriod,
  }));
};

export const getMetricValue = (
  metrics: MerchantInsightsMetrics | null,
  metricType: MetricType,
): number => {
  if (!metrics) return 0;

  switch (metricType) {
    case 'gmv':
      return metrics.gmv;
    case 'transactionCount':
      return metrics.transactionCount;
    case 'averageOrderValue':
      return metrics.averageOrderValue;
    case 'authorizationRate':
      return metrics.authorizationRate;
    case 'contributingMerchants':
      return metrics.contributingMerchants;
    case 'refundGmv':
      return metrics.refundGmv;
    case 'refundTransactionCount':
      return metrics.refundTransactionCount;
    default:
      return 0;
  }
};

// Central function that handles null checks and delegates to appropriate formatter
export const formatChartValue = (
  value: number | null | undefined,
  metricType: MetricType,
): string => {
  if (value == null) return '-';

  switch (metricType) {
    case 'gmv':
    case 'averageOrderValue':
    case 'refundGmv':
      return formatCurrency(value);
    case 'transactionCount':
    case 'contributingMerchants':
    case 'refundTransactionCount':
      return formatNumber(value);
    case 'authorizationRate':
      return formatPercentage(value);
    default:
      return value.toString();
  }
};

export const getMetricDisplayName = (metricType: MetricType): string => {
  switch (metricType) {
    case 'gmv':
      return 'Gross Merchandise Value (GMV)';
    case 'transactionCount':
      return 'Transaction Count';
    case 'averageOrderValue':
      return 'Average Order Value (AOV)';
    case 'authorizationRate':
      return 'Authorization Rate';
    case 'contributingMerchants':
      return 'Contributing Merchants';
    case 'refundGmv':
      return 'Successful Refund Value';
    case 'refundTransactionCount':
      return 'Refund Transaction Count';
    default:
      return metricType;
  }
};
